import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationApi } from '../utils/api';
import { Bell, Check, Trash2, Mail } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Notifications() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await notificationApi.getAll({ limit: 50 });
      return response.data;
    },
  });

  const markReadMutation = useMutation({
    mutationFn: (id: number) => notificationApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('所有通知已標記為已讀');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => notificationApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'transfer':
        return '💸';
      case 'grade':
        return '📝';
      case 'fee':
        return '💰';
      case 'system':
        return '⚙️';
      default:
        return '📢';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Bell className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">通知中心</h1>
        </div>
        {data?.unreadCount > 0 && (
          <button
            className="btn btn-sm btn-primary"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
          >
            <Check className="h-4 w-4 mr-1" />
            全部標記已讀
          </button>
        )}
      </div>

      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          ) : data?.notifications?.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">暫無通知</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data?.notifications?.map((notif: any) => (
                <div
                  key={notif.id}
                  className={`p-4 rounded-lg border-l-4 ${
                    notif.is_read
                      ? 'bg-base-200 border-gray-300'
                      : 'bg-primary/5 border-primary'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{getTypeIcon(notif.type)}</span>
                      <div className="flex-1">
                        <h3 className={`font-semibold ${notif.is_read ? '' : 'text-primary'}`}>
                          {notif.title || '通知'}
                          {!notif.is_read && (
                            <span className="badge badge-sm badge-primary ml-2">未讀</span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">{notif.content}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          來自: {notif.sender_name} • {new Date(notif.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!notif.is_read && (
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => markReadMutation.mutate(notif.id)}
                          title="標記為已讀"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-ghost text-error"
                        onClick={() => deleteMutation.mutate(notif.id)}
                        title="刪除"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
